import { useState } from "react";
import { useTranslation } from "react-i18next";
import ScreenshotPlaceholder from "../components/ScreenshotPlaceholder";
import "katex/dist/katex.min.css";
import { BlockMath } from "react-katex";
import { styles, cx } from "../styles";
import { useThemeClasses } from "../providers/ThemeProvider";

const HelpView = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState("help");
  const themeClasses = useThemeClasses();

  const sections = [
    { id: "help", label: t("helpPage.sections.help"), icon: "📖" },
    {
      id: "calculation",
      label: t("helpPage.sections.calculation"),
      icon: "🧮",
    },
    { id: "whatsNew", label: t("helpPage.sections.whatsNew"), icon: "🆕" },
    { id: "contact", label: t("helpPage.sections.contact"), icon: "📧" },
  ];

  const kpis = [
    {
      name: t("helpPage.calculation.kpis.oee.name"),
      category: "Performance",
      icon: "📊",
      color: themeClasses.bgInfo,
      formula: String.raw`
        OEE = Uptime \cdot Performance \cdot \frac{1}{Reject\ rate}
      `,
      description: t("helpPage.calculation.kpis.oee.description"),
      highlight:
        "Primary performance indicator combining availability, performance, and quality metrics.",
    },
    {
      name: t("helpPage.calculation.kpis.efficiency.name"),
      category: "Performance",
      icon: "⚡",
      color: themeClasses.bgSuccess,
      formula: String.raw`
        Efficiency = \frac{output_{total}}{(t_{theor.} - t_{stopExt} - t_{planned}) \cdot n}
      `,
      description: t("helpPage.calculation.kpis.efficiency.description"),
      highlight:
        "Measures how efficiently the machine operates during available time.",
    },
    {
      name: t("helpPage.calculation.kpis.utilization.name"),
      category: "Performance",
      icon: "🎯",
      color: themeClasses.bgBrandLight,
      formula: String.raw`
        Utilization = \frac{output_{total}}{(Theoretical\ production)}
      `,
      description: t("helpPage.calculation.kpis.utilization.description"),
      highlight: "Shows the ratio of actual to theoretical maximum production.",
    },
    {
      name: t("helpPage.calculation.kpis.output.name"),
      category: "Production",
      icon: "📦",
      color: themeClasses.bgInfo,
      formula: String.raw`
        output_{total} = \sum \Delta output
      `,
      description: t("helpPage.calculation.kpis.output.description"),
      highlight: "Total cumulative output over the selected time period.",
    },
    {
      name: t("helpPage.calculation.kpis.downtime.name"),
      category: "Availability",
      icon: "⏸️",
      color: themeClasses.bgError,
      formula: String.raw`
        \text{Downtime} =
        \text{Geplante Stillstandszeit} + \\
        \text{Externe Stillstandszeit}
      `,
      description: t("helpPage.calculation.kpis.downtime.description"),
      highlight: "Combines planned maintenance and external downtime periods.",
    },
    {
      name: t("helpPage.calculation.kpis.runtime.name"),
      category: "Availability",
      icon: "⏱️",
      color: themeClasses.bgSuccess,
      formula: String.raw`\text{Laufzeit} = \text{Verfügbare Zeit} - \text{Downtime}`,
      description: t("helpPage.calculation.kpis.runtime.description"),
      highlight: "Actual operating time after subtracting all downtime.",
    },
    {
      name: t("helpPage.calculation.kpis.maxRuntime.name"),
      category: "Availability",
      icon: "⏰",
      color: themeClasses.textMuted.replace("text-", "bg-"),
      formula: String.raw`\text{Max. Laufzeit} = \text{Verfügbare Zeit}`,
      description: t("helpPage.calculation.kpis.maxRuntime.description"),
      highlight: "Maximum possible operating time within the selected period.",
    },
    {
      name: t("helpPage.calculation.kpis.mtbf.name"),
      category: "Reliability",
      icon: "🔧",
      color: themeClasses.bgWarning,
      formula: String.raw`MTBF = \frac{runtime_{total}}{stops_{total}}`,
      description: t("helpPage.calculation.kpis.mtbf.description"),
      highlight: "Mean Time Between Failures - reliability indicator.",
    },
    {
      name: t("helpPage.calculation.kpis.rejectRate.name"),
      category: "Quality",
      icon: "❌",
      color: themeClasses.bgError,
      formula: String.raw`
        Reject\ rate = \frac{rejects_{total}}{(output_{total} + rejects_{total})}
      `,
      description: t("helpPage.calculation.kpis.rejectRate.description"),
      highlight: "Percentage of products that do not meet quality standards.",
    },
    {
      name: t("helpPage.calculation.kpis.externalDowntime.name"),
      category: "Availability",
      icon: "🔌",
      color: themeClasses.bgWarning,
      formula: String.raw`
        t_{stopExt} = \sum \Delta downtime_{external}
      `,
      description: t("helpPage.calculation.kpis.externalDowntime.description"),
      highlight:
        "Downtime caused by external factors (power, materials, etc.).",
    },
    {
      name: t("helpPage.calculation.kpis.plannedDowntime.name"),
      category: "Availability",
      icon: "🗓️",
      color: themeClasses.bgInfo,
      formula: String.raw`
        t_{planned} = \sum \Delta downtime_{planned}
      `,
      description: t("helpPage.calculation.kpis.plannedDowntime.description"),
      highlight: "Scheduled maintenance and planned production stops.",
    },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case "help":
        return (
          <div className={styles.help.content.container}>
            <div>
              <h1
                className={cx(
                  styles.help.content.pageTitle,
                  themeClasses.textPrimary,
                )}
              >
                {t("helpPage.welcome.title")}
              </h1>
              <p
                className={cx(
                  styles.help.content.paragraphWithMargin,
                  themeClasses.textSecondary,
                )}
              >
                {t("helpPage.welcome.description")}
              </p>
            </div>
            <div
              className={cx(
                themeClasses.bgSecondary,
                themeClasses.borderPrimary,
                "border rounded-lg p-4 mb-6",
              )}
            >
              <h2
                className={cx(
                  styles.help.content.sectionTitle,
                  themeClasses.textPrimary,
                )}
              >
                {t("helpPage.welcome.tableOfContents")}
              </h2>
              <ol
                className={cx(
                  styles.help.content.tableOfContents,
                  themeClasses.textSecondary,
                )}
              >
                <li>
                  <a
                    href="#erste-schritte"
                    className={cx(
                      styles.help.content.tocLink,
                      themeClasses.linkPrimary,
                      themeClasses.linkHover,
                    )}
                  >
                    {t("helpPage.welcome.firstSteps")}
                  </a>
                </li>
                <li>
                  <a
                    href="#performance-cockpit"
                    className={cx(
                      styles.help.content.tocLink,
                      themeClasses.linkPrimary,
                      themeClasses.linkHover,
                    )}
                  >
                    {t("helpPage.welcome.performanceCockpit")}
                  </a>
                </li>
                <li>
                  <a
                    href="#datenvisualisierung"
                    className={cx(
                      styles.help.content.tocLink,
                      themeClasses.linkPrimary,
                      themeClasses.linkHover,
                    )}
                  >
                    {t("helpPage.welcome.dataVisualization")}
                  </a>
                </li>
                <li>
                  <a
                    href="#datenaktualisierung"
                    className={cx(
                      styles.help.content.tocLink,
                      themeClasses.linkPrimary,
                      themeClasses.linkHover,
                    )}
                  >
                    {t("helpPage.welcome.dataRefresh")}
                  </a>
                </li>
                <li>
                  <a
                    href="#faq"
                    className={cx(
                      styles.help.content.tocLink,
                      themeClasses.linkPrimary,
                      themeClasses.linkHover,
                    )}
                  >
                    {t("helpPage.welcome.faq")}
                  </a>
                </li>
              </ol>
            </div>
            <div
              className={cx(
                styles.help.sections.quickStart.container,
                themeClasses.sectionBlue,
                "border",
              )}
            >
              <h2
                className={cx(
                  styles.help.infoBox.titleWithIcon,
                  themeClasses.textPrimary,
                )}
              >
                🚀 {t("helpPage.quickStart.title")}
              </h2>
              <p
                className={cx(
                  styles.help.content.paragraphWithMargin,
                  themeClasses.textSecondary,
                )}
              >
                {t("helpPage.quickStart.description")}
              </p>

              <div className={styles.help.content.quickStartGrid}>
                <div
                  className={cx(
                    styles.help.content.quickStartCard,
                    themeClasses.bgSurface,
                    themeClasses.borderPrimary,
                    "border",
                  )}
                >
                  <h3
                    className={cx(
                      styles.help.content.quickStartCardTitle,
                      themeClasses.textSuccess,
                      themeClasses.textPrimary,
                    )}
                  >
                    🎯 {t("helpPage.quickStart.checkMachineStatus.title")}
                  </h3>
                  <ol
                    className={cx(
                      styles.help.content.quickStartCardSteps,
                      themeClasses.textSecondary,
                    )}
                  >
                    {(
                      t("helpPage.quickStart.checkMachineStatus.steps", {
                        returnObjects: true,
                      }) as string[]
                    ).map((step: string, index: number) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
                <div
                  className={cx(
                    styles.help.content.quickStartCard,
                    themeClasses.bgSurface,
                    themeClasses.borderPrimary,
                    "border",
                  )}
                >
                  <h3
                    className={cx(
                      styles.help.content.quickStartCardTitle,
                      themeClasses.textInfo,
                      themeClasses.textPrimary,
                    )}
                  >
                    📊 {t("helpPage.quickStart.analyzeMachineData.title")}
                  </h3>
                  <ol
                    className={cx(
                      styles.help.content.quickStartCardSteps,
                      themeClasses.textSecondary,
                    )}
                  >
                    {(
                      t("helpPage.quickStart.analyzeMachineData.steps", {
                        returnObjects: true,
                      }) as string[]
                    ).map((step: string, index: number) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
                <div
                  className={cx(
                    styles.help.content.quickStartCard,
                    themeClasses.bgSurface,
                    themeClasses.borderPrimary,
                    "border",
                  )}
                >
                  <h3
                    className={cx(
                      styles.help.content.quickStartCardTitle,
                      themeClasses.textPurple,
                      themeClasses.textPrimary,
                    )}
                  >
                    🔍 {t("helpPage.quickStart.findSpecificMachine.title")}
                  </h3>
                  <ol
                    className={cx(
                      styles.help.content.quickStartCardSteps,
                      themeClasses.textSecondary,
                    )}
                  >
                    {(
                      t("helpPage.quickStart.findSpecificMachine.steps", {
                        returnObjects: true,
                      }) as string[]
                    ).map((step: string, index: number) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
                <div
                  className={cx(
                    styles.help.content.quickStartCard,
                    themeClasses.bgSurface,
                    themeClasses.borderPrimary,
                    "border",
                  )}
                >
                  <h3
                    className={cx(
                      styles.help.content.quickStartCardTitle,
                      themeClasses.textOrange,
                      themeClasses.textPrimary,
                    )}
                  >
                    🕐 {t("helpPage.quickStart.compareShiftData.title")}
                  </h3>
                  <ol
                    className={cx(
                      styles.help.content.quickStartCardSteps,
                      themeClasses.textSecondary,
                    )}
                  >
                    {(
                      t("helpPage.quickStart.compareShiftData.steps", {
                        returnObjects: true,
                      }) as string[]
                    ).map((step: string, index: number) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
            <hr
              className={cx(
                styles.help.content.divider,
                themeClasses.borderSecondary,
              )}
            />
            <div id="erste-schritte">
              <h2
                className={cx(
                  styles.help.content.sectionTitle,
                  themeClasses.textPrimary,
                )}
              >
                {t("helpPage.firstSteps.title")}
              </h2>
              <div
                className={cx(
                  styles.help.sections.blue,
                  themeClasses.sectionBlue,
                  "border",
                )}
              >
                <h3
                  className={cx(
                    styles.help.infoBox.titleWithIcon,
                    themeClasses.textPrimary,
                  )}
                >
                  🚀 {t("helpPage.firstSteps.welcomeSection.title")}
                </h3>
                <p
                  className={cx(
                    styles.help.content.paragraphWithMargin,
                    themeClasses.textSecondary,
                  )}
                >
                  {t("helpPage.firstSteps.welcomeSection.description")}
                </p>

                <div
                  className={cx(
                    styles.help.infoBox.highlighted,
                    themeClasses.bgSurface,
                    themeClasses.borderPrimary,
                    "border",
                  )}
                >
                  <h4
                    className={cx(
                      styles.help.infoBox.subtitle,
                      themeClasses.textPrimary,
                    )}
                  >
                    {t("helpPage.firstSteps.welcomeSection.whatYouSee.title")}
                  </h4>
                  <ul
                    className={cx(
                      styles.help.lists.simple,
                      themeClasses.textSecondary,
                    )}
                  >
                    {(
                      t("helpPage.firstSteps.welcomeSection.whatYouSee.items", {
                        returnObjects: true,
                      }) as string[]
                    ).map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>

                <ScreenshotPlaceholder
                  title="Startseite - Produktionslinien-Übersicht"
                  description="Zeigt die Hauptnavigation, Suchfeld, Filter-Optionen und die Tree-/Card-/Globe-Ansichten"
                />
              </div>
              <div
                className={cx(
                  styles.help.sections.purple,
                  themeClasses.sectionPurple,
                  "border",
                )}
              >
                <h3
                  className={cx(
                    styles.help.infoBox.titleWithIcon,
                    themeClasses.textPrimary,
                  )}
                >
                  📊 {t("helpPage.firstSteps.recentlyAll.title")}
                </h3>
                <p
                  className={cx(
                    styles.help.content.paragraphWithMargin,
                    themeClasses.textSecondary,
                  )}
                >
                  {t("helpPage.firstSteps.recentlyAll.description")}
                </p>

                <div className={styles.help.content.quickStartGrid}>
                  <div
                    className={cx(
                      styles.help.content.quickStartCard,
                      themeClasses.bgSurface,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <h4
                      className={cx(
                        styles.help.content.quickStartCardTitle,
                        themeClasses.textPurple,
                        themeClasses.textPrimary,
                      )}
                    >
                      📖 {t("helpPage.firstSteps.recentlyAll.recently.title")}
                    </h4>
                    <p
                      className={cx(
                        styles.help.content.quickStartCardDescription,
                        themeClasses.textSecondary,
                      )}
                    >
                      {t(
                        "helpPage.firstSteps.recentlyAll.recently.description",
                      )}
                    </p>
                  </div>
                  <div
                    className={cx(
                      styles.help.content.quickStartCard,
                      themeClasses.bgSurface,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <h4
                      className={cx(
                        styles.help.content.quickStartCardTitle,
                        themeClasses.textPurple,
                        themeClasses.textPrimary,
                      )}
                    >
                      📋 {t("helpPage.firstSteps.recentlyAll.all.title")}
                    </h4>
                    <p
                      className={cx(
                        styles.help.content.quickStartCardDescription,
                        themeClasses.textSecondary,
                      )}
                    >
                      {t("helpPage.firstSteps.recentlyAll.all.description")}
                    </p>
                  </div>
                </div>

                <ScreenshotPlaceholder
                  title="Recently vs All - Ansichtswechsel"
                  description="Zeigt den Unterschied zwischen Recently (persönlicher Verlauf) und All (Gesamtübersicht)"
                />
              </div>
              <div
                className={cx(
                  styles.help.sections.yellow,
                  themeClasses.sectionYellow,
                  "border",
                )}
              >
                <h3
                  className={cx(
                    styles.help.infoBox.titleWithIcon,
                    themeClasses.textPrimary,
                  )}
                >
                  🗺️ {t("helpPage.firstSteps.navigationMethods.title")}
                </h3>
                <p
                  className={cx(
                    styles.help.content.paragraphWithMargin,
                    themeClasses.textSecondary,
                  )}
                >
                  {t("helpPage.firstSteps.navigationMethods.description")}
                </p>

                <div className={styles.help.content.threeColumnGrid}>
                  <div
                    className={cx(
                      styles.help.content.quickStartCard,
                      themeClasses.bgSurface,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <h4
                      className={cx(
                        styles.help.content.quickStartCardTitle,
                        themeClasses.textWarning,
                        themeClasses.textPrimary,
                      )}
                    >
                      🌲{" "}
                      {t(
                        "helpPage.firstSteps.navigationMethods.treeView.title",
                      )}
                    </h4>
                    <p
                      className={cx(
                        styles.help.content.quickStartCardDescription,
                        themeClasses.textSecondary,
                      )}
                    >
                      {t(
                        "helpPage.firstSteps.navigationMethods.treeView.description",
                      )}
                    </p>
                  </div>
                  <div
                    className={cx(
                      styles.help.content.quickStartCard,
                      themeClasses.bgSurface,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <h4
                      className={cx(
                        styles.help.content.quickStartCardTitle,
                        themeClasses.textWarning,
                        themeClasses.textPrimary,
                      )}
                    >
                      🃏{" "}
                      {t(
                        "helpPage.firstSteps.navigationMethods.cardView.title",
                      )}
                    </h4>
                    <p
                      className={cx(
                        styles.help.content.quickStartCardDescription,
                        themeClasses.textSecondary,
                      )}
                    >
                      {t(
                        "helpPage.firstSteps.navigationMethods.cardView.description",
                      )}
                    </p>
                  </div>
                  <div
                    className={cx(
                      styles.help.content.quickStartCard,
                      themeClasses.bgSurface,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <h4
                      className={cx(
                        styles.help.content.quickStartCardTitle,
                        themeClasses.textWarning,
                        themeClasses.textPrimary,
                      )}
                    >
                      🌍{" "}
                      {t(
                        "helpPage.firstSteps.navigationMethods.globeView.title",
                      )}
                    </h4>
                    <p
                      className={cx(
                        styles.help.content.quickStartCardDescription,
                        themeClasses.textSecondary,
                      )}
                    >
                      {t(
                        "helpPage.firstSteps.navigationMethods.globeView.description",
                      )}
                    </p>
                  </div>
                </div>

                <ScreenshotPlaceholder
                  title="Navigation Views - Tree, Card, Globe"
                  description="Zeigt die drei verschiedenen Navigationsmethoden und ihre jeweiligen Vorteile"
                />
              </div>
              <div
                className={cx(
                  styles.help.sections.red,
                  themeClasses.sectionRed,
                  "border",
                )}
              >
                <h3
                  className={cx(
                    styles.help.infoBox.titleWithIcon,
                    themeClasses.textPrimary,
                  )}
                >
                  🚦 {t("helpPage.firstSteps.machineOverview.title")}
                </h3>
                <p
                  className={cx(
                    styles.help.content.paragraphWithMargin,
                    themeClasses.textSecondary,
                  )}
                >
                  {t("helpPage.firstSteps.machineOverview.description")}
                </p>
                <div className={styles.help.statusColors.container}>
                  <div className={styles.help.statusColors.item}>
                    <span
                      className={cx(
                        styles.help.statusColors.indicator,
                        themeClasses.bgSuccess,
                      )}
                    ></span>
                    <span
                      className={cx(
                        styles.help.statusColors.text,
                        themeClasses.textSecondary,
                      )}
                    >
                      {t(
                        "helpPage.firstSteps.machineOverview.statusColors.green",
                      )}
                    </span>
                  </div>
                  <div className={styles.help.statusColors.item}>
                    <span
                      className={cx(
                        styles.help.statusColors.indicator,
                        themeClasses.bgWarning,
                      )}
                    ></span>
                    <span
                      className={cx(
                        styles.help.statusColors.text,
                        themeClasses.textSecondary,
                      )}
                    >
                      {t(
                        "helpPage.firstSteps.machineOverview.statusColors.yellow",
                      )}
                    </span>
                  </div>
                  <div className={styles.help.statusColors.item}>
                    <span
                      className={cx(
                        styles.help.statusColors.indicator,
                        themeClasses.bgError,
                      )}
                    ></span>
                    <span
                      className={cx(
                        styles.help.statusColors.text,
                        themeClasses.textSecondary,
                      )}
                    >
                      {t(
                        "helpPage.firstSteps.machineOverview.statusColors.red",
                      )}
                    </span>
                  </div>
                  <div className={styles.help.statusColors.item}>
                    <span
                      className={cx(
                        styles.help.statusColors.indicator,
                        themeClasses.bgGray,
                      )}
                    ></span>
                    <span
                      className={cx(
                        styles.help.statusColors.text,
                        themeClasses.textSecondary,
                      )}
                    >
                      {t(
                        "helpPage.firstSteps.machineOverview.statusColors.gray",
                      )}
                    </span>
                  </div>
                </div>

                <ScreenshotPlaceholder
                  title="Maschinen-Übersicht mit Farbkodierung"
                  description="Zeigt verschiedene Maschinenstatus und die klickbaren Maschinen-Karten"
                />
              </div>{" "}
              <div
                className={cx(
                  styles.help.sections.blue,
                  themeClasses.sectionGreen,
                  "border",
                )}
              >
                <h3
                  className={cx(
                    styles.help.infoBox.titleWithIcon,
                    themeClasses.textPrimary,
                  )}
                >
                  📈 {t("helpPage.firstSteps.analysisAreas.title")}
                </h3>
                <p
                  className={cx(
                    styles.help.content.paragraphWithMargin,
                    themeClasses.textSecondary,
                  )}
                >
                  {t("helpPage.firstSteps.analysisAreas.description")}
                </p>

                <div className={styles.help.content.quickStartGrid}>
                  <div
                    className={cx(
                      styles.help.content.quickStartCard,
                      themeClasses.bgSurface,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <h4
                      className={cx(
                        styles.help.content.quickStartCardTitle,
                        themeClasses.textSuccess,
                        themeClasses.textPrimary,
                      )}
                    >
                      📈{" "}
                      {t("helpPage.firstSteps.analysisAreas.efficiency.title")}
                    </h4>
                    <p
                      className={cx(
                        styles.help.content.quickStartCardDescription,
                        themeClasses.textSecondary,
                      )}
                    >
                      <strong>
                        {t(
                          "helpPage.firstSteps.analysisAreas.efficiency.description",
                        )}
                      </strong>
                    </p>
                    <ul
                      className={cx(
                        styles.help.lists.simple,
                        themeClasses.textSecondary,
                      )}
                    >
                      {(
                        t(
                          "helpPage.firstSteps.analysisAreas.efficiency.items",
                          { returnObjects: true },
                        ) as string[]
                      ).map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div
                    className={cx(
                      styles.help.content.quickStartCard,
                      themeClasses.bgSurface,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <h4
                      className={cx(
                        styles.help.content.quickStartCardTitle,
                        themeClasses.textInfo,
                        themeClasses.textPrimary,
                      )}
                    >
                      📦 {t("helpPage.firstSteps.analysisAreas.output.title")}
                    </h4>
                    <p
                      className={cx(
                        styles.help.content.quickStartCardDescription,
                        themeClasses.textSecondary,
                      )}
                    >
                      <strong>
                        {t(
                          "helpPage.firstSteps.analysisAreas.output.description",
                        )}
                      </strong>
                    </p>
                    <ul
                      className={cx(
                        styles.help.lists.simple,
                        themeClasses.textSecondary,
                      )}
                    >
                      {(
                        t("helpPage.firstSteps.analysisAreas.output.items", {
                          returnObjects: true,
                        }) as string[]
                      ).map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div
                    className={cx(
                      styles.help.content.quickStartCard,
                      themeClasses.bgSurface,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <h4
                      className={cx(
                        styles.help.content.quickStartCardTitle,
                        themeClasses.textError,
                        themeClasses.textPrimary,
                      )}
                    >
                      ⏸️ {t("helpPage.firstSteps.analysisAreas.downtime.title")}
                    </h4>
                    <p
                      className={cx(
                        styles.help.content.quickStartCardDescription,
                        themeClasses.textSecondary,
                      )}
                    >
                      <strong>
                        {t(
                          "helpPage.firstSteps.analysisAreas.downtime.description",
                        )}
                      </strong>
                    </p>
                    <ul
                      className={cx(
                        styles.help.lists.simple,
                        themeClasses.textSecondary,
                      )}
                    >
                      {(
                        t("helpPage.firstSteps.analysisAreas.downtime.items", {
                          returnObjects: true,
                        }) as string[]
                      ).map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div
                    className={cx(
                      styles.help.content.quickStartCard,
                      themeClasses.bgSurface,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <h4
                      className={cx(
                        styles.help.content.quickStartCardTitle,
                        themeClasses.textOrange,
                        themeClasses.textPrimary,
                      )}
                    >
                      🎯 {t("helpPage.firstSteps.analysisAreas.mtbf.title")}
                    </h4>
                    <p
                      className={cx(
                        styles.help.content.quickStartCardDescription,
                        themeClasses.textSecondary,
                      )}
                    >
                      <strong>
                        {t(
                          "helpPage.firstSteps.analysisAreas.mtbf.description",
                        )}
                      </strong>
                    </p>
                    <ul
                      className={cx(
                        styles.help.lists.simple,
                        themeClasses.textSecondary,
                      )}
                    >
                      {(
                        t("helpPage.firstSteps.analysisAreas.mtbf.items", {
                          returnObjects: true,
                        }) as string[]
                      ).map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <ScreenshotPlaceholder
                  title="Maschinen-Dashboard mit 4 Hauptbereichen"
                  description="Zeigt die vier Analyse-Tabs: Efficiency, Output, Downtime und MTBF"
                />
              </div>
            </div>{" "}
            <hr
              className={cx(
                styles.help.content.divider,
                themeClasses.borderSecondary,
              )}
            />
            <div id="datenvisualisierung">
              <h2
                className={cx(
                  styles.help.content.sectionTitle,
                  themeClasses.textPrimary,
                )}
              >
                {t("helpPage.dataVisualization.title")}
              </h2>

              <div
                className={cx(
                  styles.help.sections.blue,
                  themeClasses.sectionBlue,
                  "border",
                )}
              >
                <h3
                  className={cx(
                    styles.help.infoBox.titleWithIcon,
                    themeClasses.textPrimary,
                  )}
                >
                  📊 {t("helpPage.dataVisualization.chartsAndGraphs.title")}
                </h3>
                <p
                  className={cx(
                    styles.help.content.paragraphWithMargin,
                    themeClasses.textSecondary,
                  )}
                >
                  {t("helpPage.dataVisualization.chartsAndGraphs.description")}
                </p>

                <div className={styles.help.content.threeColumnGrid}>
                  <div
                    className={cx(
                      styles.help.content.quickStartCard,
                      themeClasses.bgSurface,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <h4
                      className={cx(
                        styles.help.content.quickStartCardTitle,
                        themeClasses.textInfo,
                        themeClasses.textPrimary,
                      )}
                    >
                      📈{" "}
                      {t(
                        "helpPage.dataVisualization.chartsAndGraphs.lineCharts.title",
                      )}
                    </h4>
                    <p
                      className={cx(
                        styles.help.content.quickStartCardDescription,
                        themeClasses.textSecondary,
                      )}
                    >
                      {t(
                        "helpPage.dataVisualization.chartsAndGraphs.lineCharts.description",
                      )}
                    </p>
                  </div>
                  <div
                    className={cx(
                      styles.help.content.quickStartCard,
                      themeClasses.bgSurface,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <h4
                      className={cx(
                        styles.help.content.quickStartCardTitle,
                        themeClasses.textInfo,
                        themeClasses.textPrimary,
                      )}
                    >
                      📊{" "}
                      {t(
                        "helpPage.dataVisualization.chartsAndGraphs.barCharts.title",
                      )}
                    </h4>
                    <p
                      className={cx(
                        styles.help.content.quickStartCardDescription,
                        themeClasses.textSecondary,
                      )}
                    >
                      {t(
                        "helpPage.dataVisualization.chartsAndGraphs.barCharts.description",
                      )}
                    </p>
                  </div>
                  <div
                    className={cx(
                      styles.help.content.quickStartCard,
                      themeClasses.bgSurface,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <h4
                      className={cx(
                        styles.help.content.quickStartCardTitle,
                        themeClasses.textInfo,
                        themeClasses.textPrimary,
                      )}
                    >
                      🥧{" "}
                      {t(
                        "helpPage.dataVisualization.chartsAndGraphs.pieCharts.title",
                      )}
                    </h4>
                    <p
                      className={cx(
                        styles.help.content.quickStartCardDescription,
                        themeClasses.textSecondary,
                      )}
                    >
                      {t(
                        "helpPage.dataVisualization.chartsAndGraphs.pieCharts.description",
                      )}
                    </p>
                  </div>
                </div>

                <ScreenshotPlaceholder
                  title="Chart Types Overview"
                  description="Shows different chart types used in the dashboard"
                />
              </div>

              <div
                className={cx(
                  styles.help.sections.yellow,
                  themeClasses.sectionYellow,
                  "border",
                )}
              >
                <h3
                  className={cx(
                    styles.help.infoBox.titleWithIcon,
                    themeClasses.textPrimary,
                  )}
                >
                  🎮 {t("helpPage.dataVisualization.interactiveFeatures.title")}
                </h3>
                <p
                  className={cx(
                    styles.help.content.paragraphWithMargin,
                    themeClasses.textSecondary,
                  )}
                >
                  {t(
                    "helpPage.dataVisualization.interactiveFeatures.description",
                  )}
                </p>

                <ul
                  className={cx(
                    styles.help.lists.bulleted,
                    themeClasses.textSecondary,
                  )}
                >
                  {(
                    t(
                      "helpPage.dataVisualization.interactiveFeatures.features",
                      { returnObjects: true },
                    ) as string[]
                  ).map((feature: string, index: number) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>

                <ScreenshotPlaceholder
                  title="Interactive Chart Features"
                  description="Shows hover tooltips, zoom functions, and export options"
                />
              </div>

              <div
                className={cx(
                  styles.help.sections.purple,
                  themeClasses.sectionPurple,
                  "border",
                )}
              >
                <h3
                  className={cx(
                    styles.help.infoBox.titleWithIcon,
                    themeClasses.textPrimary,
                  )}
                >
                  ⏰ {t("helpPage.dataVisualization.timeRangeSelection.title")}
                </h3>
                <p
                  className={cx(
                    styles.help.content.paragraphWithMargin,
                    themeClasses.textSecondary,
                  )}
                >
                  {t(
                    "helpPage.dataVisualization.timeRangeSelection.description",
                  )}
                </p>

                <div className={styles.help.content.quickStartGrid}>
                  <div
                    className={cx(
                      styles.help.content.quickStartCard,
                      themeClasses.bgSurface,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <h4
                      className={cx(
                        styles.help.content.quickStartCardTitle,
                        themeClasses.textPrimary,
                      )}
                    >
                      {t(
                        "helpPage.dataVisualization.timeRangeSelection.presets.title",
                      )}
                    </h4>
                    <ul
                      className={cx(
                        styles.help.lists.simple,
                        themeClasses.textSecondary,
                      )}
                    >
                      {(
                        t(
                          "helpPage.dataVisualization.timeRangeSelection.presets.items",
                          { returnObjects: true },
                        ) as string[]
                      ).map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div
                    className={cx(
                      styles.help.content.quickStartCard,
                      themeClasses.bgSurface,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <h4
                      className={cx(
                        styles.help.content.quickStartCardTitle,
                        themeClasses.textPrimary,
                      )}
                    >
                      {t(
                        "helpPage.dataVisualization.timeRangeSelection.shiftFilter.title",
                      )}
                    </h4>
                    <p
                      className={cx(
                        styles.help.content.quickStartCardDescription,
                        themeClasses.textSecondary,
                      )}
                    >
                      {t(
                        "helpPage.dataVisualization.timeRangeSelection.shiftFilter.description",
                      )}
                    </p>
                  </div>
                </div>

                <ScreenshotPlaceholder
                  title="Time Range and Shift Filters"
                  description="Shows date selection and shift filter controls"
                />
              </div>
            </div>
            <hr
              className={cx(
                styles.help.content.divider,
                themeClasses.borderSecondary,
              )}
            />
            <div id="datenaktualisierung">
              <h2
                className={cx(
                  styles.help.content.sectionTitle,
                  themeClasses.textPrimary,
                )}
              >
                {t("helpPage.dataRefresh.title")}
              </h2>

              <div
                className={cx(
                  styles.help.sections.blue,
                  themeClasses.sectionBlue,
                  "border",
                )}
              >
                <h3
                  className={cx(
                    styles.help.infoBox.titleWithIcon,
                    themeClasses.textPrimary,
                  )}
                >
                  🔄 {t("helpPage.dataRefresh.automaticRefresh.title")}
                </h3>
                <p
                  className={cx(
                    styles.help.content.paragraphWithMargin,
                    themeClasses.textSecondary,
                  )}
                >
                  {t("helpPage.dataRefresh.automaticRefresh.description")}
                </p>

                <ul
                  className={cx(
                    styles.help.lists.bulleted,
                    themeClasses.textSecondary,
                  )}
                >
                  {(
                    t("helpPage.dataRefresh.automaticRefresh.features", {
                      returnObjects: true,
                    }) as string[]
                  ).map((feature: string, index: number) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>

                <ScreenshotPlaceholder
                  title="Live Data Updates"
                  description="Shows connection status and automatic data refresh indicators"
                />
              </div>

              <div
                className={cx(
                  styles.help.sections.yellow,
                  themeClasses.sectionYellow,
                  "border",
                )}
              >
                <h3
                  className={cx(
                    styles.help.infoBox.titleWithIcon,
                    themeClasses.textPrimary,
                  )}
                >
                  🔧 {t("helpPage.dataRefresh.manualRefresh.title")}
                </h3>
                <p
                  className={cx(
                    styles.help.content.paragraphWithMargin,
                    themeClasses.textSecondary,
                  )}
                >
                  {t("helpPage.dataRefresh.manualRefresh.description")}
                </p>

                <ul
                  className={cx(
                    styles.help.lists.simple,
                    themeClasses.textSecondary,
                  )}
                >
                  {(
                    t("helpPage.dataRefresh.manualRefresh.steps", {
                      returnObjects: true,
                    }) as string[]
                  ).map((step: string, index: number) => (
                    <li key={index}>{step}</li>
                  ))}
                </ul>
              </div>
            </div>
            <hr
              className={cx(
                styles.help.content.divider,
                themeClasses.borderSecondary,
              )}
            />
            <div id="faq">
              <h2
                className={cx(
                  styles.help.content.sectionTitle,
                  themeClasses.textPrimary,
                )}
              >
                {t("helpPage.faq.title")}
              </h2>

              <div
                className={cx(
                  styles.help.faq.container,
                  themeClasses.sectionBlue,
                  "border",
                )}
              >
                <div
                  className={cx(
                    styles.help.faq.item,
                    themeClasses.bgSurface,
                    themeClasses.borderPrimary,
                    "border",
                  )}
                >
                  <h3
                    className={cx(
                      styles.help.faq.question,
                      themeClasses.textPrimary,
                    )}
                  >
                    ❓ {t("helpPage.faq.findMachine.question")}
                  </h3>
                  <p
                    className={cx(
                      styles.help.faq.answer,
                      themeClasses.textSecondary,
                    )}
                  >
                    <strong>{t("helpPage.faq.findMachine.answer")}</strong>
                  </p>
                  <ul
                    className={cx(
                      styles.help.lists.simple,
                      themeClasses.textSecondary,
                    )}
                  >
                    {(
                      t("helpPage.faq.findMachine.methods", {
                        returnObjects: true,
                      }) as string[]
                    ).map((method: string, index: number) => (
                      <li key={index}>{method}</li>
                    ))}
                  </ul>
                  <div
                    className={cx(
                      styles.help.infoBox.highlight,
                      themeClasses.bgSecondary,
                      themeClasses.borderPrimary,
                      "border",
                    )}
                  >
                    <strong className={themeClasses.textPrimary}>
                      ⚡ {t("helpPage.faq.findMachine.tip")}
                    </strong>
                  </div>
                </div>

                <div
                  className={cx(
                    styles.help.faq.item,
                    themeClasses.bgSurface,
                    themeClasses.borderPrimary,
                    "border",
                  )}
                >
                  <h3
                    className={cx(
                      styles.help.faq.question,
                      themeClasses.textPrimary,
                    )}
                  >
                    ❓ {t("helpPage.faq.exportCharts.question")}
                  </h3>
                  <p
                    className={cx(
                      styles.help.faq.answer,
                      themeClasses.textSecondary,
                    )}
                  >
                    <strong>{t("helpPage.faq.exportCharts.answer")}</strong>
                  </p>
                  <ol
                    className={cx(
                      styles.help.lists.simple,
                      themeClasses.textSecondary,
                    )}
                  >
                    {(
                      t("helpPage.faq.exportCharts.steps", {
                        returnObjects: true,
                      }) as string[]
                    ).map((step: string, index: number) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>

                <div
                  className={cx(
                    styles.help.faq.item,
                    themeClasses.bgSurface,
                    themeClasses.borderPrimary,
                    "border",
                  )}
                >
                  <h3
                    className={cx(
                      styles.help.faq.question,
                      themeClasses.textPrimary,
                    )}
                  >
                    ❓ {t("helpPage.faq.symbolsMeaning.question")}
                  </h3>
                  <p
                    className={cx(
                      styles.help.faq.answer,
                      themeClasses.textSecondary,
                    )}
                  >
                    <strong>{t("helpPage.faq.symbolsMeaning.answer")}</strong>
                  </p>
                  <div className={styles.help.content.quickStartGrid}>
                    <div className={styles.help.statusColors.container}>
                      <div className={styles.help.statusColors.item}>
                        <span
                          className={cx(
                            styles.help.statusColors.indicator,
                            themeClasses.bgSuccess,
                          )}
                        ></span>
                        <span
                          className={cx(
                            styles.help.statusColors.text,
                            themeClasses.textSecondary,
                          )}
                        >
                          {t("helpPage.faq.symbolsMeaning.statusColors.green")}
                        </span>
                      </div>
                      <div className={styles.help.statusColors.item}>
                        <span
                          className={cx(
                            styles.help.statusColors.indicator,
                            themeClasses.bgError,
                          )}
                        ></span>
                        <span
                          className={cx(
                            styles.help.statusColors.text,
                            themeClasses.textSecondary,
                          )}
                        >
                          {t("helpPage.faq.symbolsMeaning.statusColors.red")}
                        </span>
                      </div>
                      <div className={styles.help.statusColors.item}>
                        <span
                          className={cx(
                            styles.help.statusColors.indicator,
                            themeClasses.bgWarning,
                          )}
                        ></span>
                        <span
                          className={cx(
                            styles.help.statusColors.text,
                            themeClasses.textSecondary,
                          )}
                        >
                          {t("helpPage.faq.symbolsMeaning.statusColors.yellow")}
                        </span>
                      </div>
                      <div className={styles.help.statusColors.item}>
                        <span
                          className={cx(
                            styles.help.statusColors.indicator,
                            themeClasses.bgGray,
                          )}
                        ></span>
                        <span
                          className={cx(
                            styles.help.statusColors.text,
                            themeClasses.textSecondary,
                          )}
                        >
                          {t("helpPage.faq.symbolsMeaning.statusColors.gray")}
                        </span>
                      </div>
                    </div>
                    <div className={styles.help.statusColors.container}>
                      <div className={styles.help.statusColors.item}>
                        <span>🔄</span>
                        <span
                          className={cx(
                            styles.help.statusColors.text,
                            themeClasses.textSecondary,
                          )}
                        >
                          {t("helpPage.faq.symbolsMeaning.symbols.refresh")}
                        </span>
                      </div>
                      <div className={styles.help.statusColors.item}>
                        <span>⚠️</span>
                        <span
                          className={cx(
                            styles.help.statusColors.text,
                            themeClasses.textSecondary,
                          )}
                        >
                          {t("helpPage.faq.symbolsMeaning.symbols.warning")}
                        </span>
                      </div>
                      <div className={styles.help.statusColors.item}>
                        <span>📊</span>
                        <span
                          className={cx(
                            styles.help.statusColors.text,
                            themeClasses.textSecondary,
                          )}
                        >
                          {t("helpPage.faq.symbolsMeaning.symbols.chart")}
                        </span>
                      </div>
                      <div className={styles.help.statusColors.item}>
                        <span>🔌</span>
                        <span
                          className={cx(
                            styles.help.statusColors.text,
                            themeClasses.textSecondary,
                          )}
                        >
                          {t("helpPage.faq.symbolsMeaning.symbols.connection")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={cx(
                    styles.help.faq.item,
                    themeClasses.bgSurface,
                    themeClasses.borderPrimary,
                    "border",
                  )}
                >
                  <h3
                    className={cx(
                      styles.help.faq.question,
                      themeClasses.textPrimary,
                    )}
                  >
                    ❓ {t("helpPage.faq.connectionIssues.question")}
                  </h3>
                  <p
                    className={cx(
                      styles.help.faq.answer,
                      themeClasses.textSecondary,
                    )}
                  >
                    <strong>{t("helpPage.faq.connectionIssues.answer")}</strong>
                  </p>
                  <ol
                    className={cx(
                      styles.help.lists.simple,
                      themeClasses.textSecondary,
                    )}
                  >
                    {(
                      t("helpPage.faq.connectionIssues.steps", {
                        returnObjects: true,
                      }) as string[]
                    ).map((step: string, index: number) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>

                <div
                  className={cx(
                    styles.help.infoBox.container,
                    themeClasses.bgSurface,
                    themeClasses.borderPrimary,
                    "border",
                  )}
                >
                  <h3
                    className={cx(
                      styles.help.infoBox.title,
                      themeClasses.textPrimary,
                    )}
                  >
                    💡 {t("helpPage.faq.needMoreHelp.title")}
                  </h3>
                  <p
                    className={cx(
                      styles.help.infoBox.content,
                      themeClasses.textSecondary,
                    )}
                  >
                    {t("helpPage.faq.needMoreHelp.description")}
                  </p>
                  <ul
                    className={cx(
                      styles.help.lists.simple,
                      themeClasses.textSecondary,
                    )}
                  >
                    {(
                      t("helpPage.faq.needMoreHelp.options", {
                        returnObjects: true,
                      }) as string[]
                    ).map((option: string, index: number) => (
                      <li key={index}>{option}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case "calculation":
        return (
          <div className={styles.help.content.container}>
            <div className="text-center mb-12">
              <h1
                className={cx(
                  styles.help.content.pageTitle,
                  themeClasses.textPrimary,
                  "text-3xl font-bold mb-4",
                )}
              >
                {t("helpPage.calculation.title")}
              </h1>
              <p
                className={cx(
                  styles.help.content.paragraphWithMargin,
                  themeClasses.textSecondary,
                  "text-lg max-w-3xl mx-auto leading-relaxed",
                )}
              >
                {t("helpPage.calculation.description")}
              </p>

              {/* Decorative line */}
              <div className="flex items-center justify-center mt-8 mb-4">
                <div
                  className={cx("h-px w-16", themeClasses.bgSecondary)}
                ></div>
                <div
                  className={cx(
                    "mx-4 w-3 h-3 rounded-full",
                    themeClasses.bgInfo,
                  )}
                ></div>
                <div
                  className={cx("h-px w-16", themeClasses.bgSecondary)}
                ></div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
              {kpis.map((kpi, index) => (
                <div
                  key={kpi.name}
                  className={cx(
                    "relative overflow-hidden rounded-2xl border-2 p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group",
                    themeClasses.bgSurface,
                    themeClasses.borderPrimary,
                  )}
                >
                  {/* Gradient accent line */}
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-green-500"></div>

                  {/* Corner decoration */}
                  <div className="absolute top-4 right-4 opacity-10 transition-all duration-500 group-hover:opacity-20 group-hover:scale-110">
                    <div
                      className={cx(
                        "w-16 h-16 rounded-full",
                        themeClasses.bgInfo,
                      )}
                    ></div>
                  </div>

                  {/* Number badge */}
                  <div className="absolute top-6 left-6">
                    <div
                      className={cx(
                        "flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold transition-all duration-300 group-hover:scale-110",
                        themeClasses.bgInfo,
                        themeClasses.textWhite,
                      )}
                    >
                      {index + 1}
                    </div>
                  </div>

                  <div className="relative z-10 pt-6">
                    {/* Header Section */}
                    <div className="mb-8">
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className={cx(
                            "px-3 py-1 rounded-full text-sm font-medium",
                            themeClasses.bgSecondary,
                            themeClasses.textSecondary,
                          )}
                        >
                          {kpi.category}
                        </span>
                      </div>
                      <h3
                        className={cx(
                          "text-2xl font-bold flex items-center gap-3",
                          themeClasses.textPrimary,
                        )}
                      >
                        <span className="text-3xl">{kpi.icon}</span>
                        {kpi.name}
                      </h3>
                    </div>

                    {/* Formula Section */}
                    <div
                      className={cx(
                        "mb-8 p-6 rounded-xl border transition-all duration-300 group-hover:shadow-md",
                        themeClasses.bgSecondary,
                        themeClasses.borderPrimary,
                      )}
                    >
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-xl">🧮</span>
                        <div
                          className={cx(
                            "text-sm font-semibold tracking-wide uppercase",
                            themeClasses.textSecondary,
                          )}
                        >
                          Formula
                        </div>
                      </div>
                      <div
                        className={cx(
                          "p-4 rounded-lg border-2 border-dashed overflow-x-auto",
                          themeClasses.bgSurface,
                          themeClasses.borderSecondary,
                        )}
                      >
                        <div
                          className={cx(
                            "text-center",
                            themeClasses.textPrimary,
                          )}
                        >
                          <BlockMath math={kpi.formula} />
                        </div>
                      </div>
                    </div>

                    {/* Description Section */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xl">📝</span>
                        <div
                          className={cx(
                            "text-sm font-semibold tracking-wide uppercase",
                            themeClasses.textSecondary,
                          )}
                        >
                          Description
                        </div>
                      </div>
                      <p
                        className={cx(
                          "text-base leading-relaxed",
                          themeClasses.textSecondary,
                        )}
                      >
                        {kpi.description}
                      </p>

                      {kpi.highlight && (
                        <div
                          className={cx(
                            "mt-6 p-5 rounded-xl border-l-4 transition-all duration-300 hover:shadow-md",
                            themeClasses.bgWarningSubtle,
                            "border-l-yellow-500",
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <span className="text-xl mt-1">💡</span>
                            <div>
                              <div
                                className={cx(
                                  "text-sm font-semibold mb-2",
                                  themeClasses.textWarning,
                                )}
                              >
                                Key Insight
                              </div>
                              <p
                                className={cx(
                                  "text-sm leading-relaxed",
                                  themeClasses.textPrimary,
                                )}
                              >
                                {kpi.highlight}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom section */}
            <div
              className={cx(
                "mt-16 p-8 rounded-2xl border-2 border-dashed text-center transition-all duration-300 hover:shadow-lg",
                themeClasses.bgSecondary,
                themeClasses.borderSecondary,
              )}
            >
              <div className="flex justify-center mb-6">
                <div
                  className={cx(
                    "flex items-center justify-center w-20 h-20 rounded-full animate-pulse",
                    themeClasses.bgSuccess,
                  )}
                >
                  <span className="text-white text-3xl">📊</span>
                </div>
              </div>
              <h3
                className={cx(
                  "text-xl font-bold mb-3",
                  themeClasses.textPrimary,
                )}
              >
                Understanding KPI Calculations
              </h3>
              <p
                className={cx(
                  "text-base leading-relaxed max-w-2xl mx-auto",
                  themeClasses.textSecondary,
                )}
              >
                These calculations form the foundation of our performance
                analytics. Each metric provides valuable insights into machine
                efficiency, productivity, and overall operational excellence.
              </p>
            </div>
          </div>
        );

      case "whatsNew":
        return (
          <div className={styles.help.content.container}>
            <div className="text-center mb-8">
              <h1
                className={cx(
                  styles.help.content.pageTitle,
                  themeClasses.textPrimary,
                )}
              >
                {t("helpPage.whatsNew.title")}
              </h1>
            </div>

            <div className="max-w-4xl mx-auto">
              <div
                className={cx(
                  "p-10 rounded-xl border-2 border-dashed text-center",
                  themeClasses.bgSecondary,
                  themeClasses.borderSecondary,
                )}
              >
                <div className="flex justify-center mb-4">
                  <div
                    className={cx(
                      "flex items-center justify-center w-14 h-14 rounded-full",
                      themeClasses.bgInfo,
                    )}
                  >
                    <span className="text-white text-2xl">🚧</span>
                  </div>
                </div>
                <h3
                  className={cx(
                    "text-lg font-semibold mb-2",
                    themeClasses.textPrimary,
                  )}
                >
                  {t("helpPage.whatsNew.underDevelopmentTitle") ||
                    "Under Development"}
                </h3>
                <p
                  className={cx(
                    "text-sm leading-relaxed",
                    themeClasses.textSecondary,
                  )}
                >
                  {t("helpPage.whatsNew.underDevelopmentMessage") ||
                    "This page is currently under development. Check back soon for updates."}
                </p>
              </div>
            </div>
          </div>
        );

      case "contact":
        return (
          <div className={styles.help.content.container}>
            <div className="text-center mb-8">
              <h1
                className={cx(
                  styles.help.content.pageTitle,
                  themeClasses.textPrimary,
                )}
              >
                {t("helpPage.contact.title")}
              </h1>
              <p
                className={cx(
                  styles.help.content.paragraphWithMargin,
                  themeClasses.textSecondary,
                )}
              >
                {t("helpPage.contact.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div
                className={cx(
                  "relative overflow-hidden rounded-xl border p-8 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 group",
                  themeClasses.bgSurface,
                  themeClasses.borderPrimary,
                )}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100/20 to-transparent rounded-bl-full transition-all duration-500 group-hover:scale-110"></div>
                <div className="relative z-10">
                  <div className="flex items-start gap-4 mb-6">
                    <div
                      className={cx(
                        "flex items-center justify-center w-12 h-12 rounded-lg transition-transform duration-300 group-hover:scale-110",
                        themeClasses.bgInfo,
                      )}
                    >
                      <span className="text-white text-xl">🔧</span>
                    </div>
                    <div className="flex-1">
                      <h3
                        className={cx(
                          "text-xl font-bold mb-2",
                          themeClasses.textPrimary,
                        )}
                      >
                        {t("helpPage.contact.technicalSupport.title")}
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={cx(
                              "w-2 h-2 rounded-full",
                              themeClasses.bgInfo,
                            )}
                          ></div>
                          <span
                            className={cx(
                              "text-sm font-medium",
                              themeClasses.textSecondary,
                            )}
                          >
                            E-Mail
                          </span>
                        </div>
                        <a
                          href={`mailto:${t("helpPage.contact.technicalSupport.email")}`}
                          className={cx(
                            "block text-lg font-medium transition-colors duration-200 hover:underline decoration-2 underline-offset-4",
                            themeClasses.linkPrimary,
                            themeClasses.linkHover,
                          )}
                        >
                          {t("helpPage.contact.technicalSupport.email")}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={cx(
                  "relative overflow-hidden rounded-xl border p-8 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 group",
                  themeClasses.bgSurface,
                  themeClasses.borderPrimary,
                )}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-100/20 to-transparent rounded-bl-full transition-all duration-500 group-hover:scale-110"></div>
                <div className="relative z-10">
                  <div className="flex items-start gap-4 mb-6">
                    <div
                      className={cx(
                        "flex items-center justify-center w-12 h-12 rounded-lg transition-transform duration-300 group-hover:scale-110",
                        themeClasses.bgSuccess,
                      )}
                    >
                      <span className="text-white text-xl">🌐</span>
                    </div>
                    <div className="flex-1">
                      <h3
                        className={cx(
                          "text-xl font-bold mb-3",
                          themeClasses.textPrimary,
                        )}
                      >
                        {t("helpPage.contact.servicePortal.title")}
                      </h3>
                      <p
                        className={cx(
                          "mb-6 leading-relaxed",
                          themeClasses.textSecondary,
                        )}
                      >
                        {t("helpPage.contact.servicePortal.description")}
                      </p>
                      <a
                        href="https://serviceportal.focke.com/de/login"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cx(
                          "inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:scale-105 hover:shadow-md group/btn",
                          themeClasses.bgSuccess,
                          themeClasses.textWhite,
                        )}
                      >
                        <span>
                          {t("helpPage.contact.servicePortal.buttonText")}
                        </span>
                        <span className="transition-transform duration-200 group-hover/btn:translate-x-1">
                          ↗
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className={cx(
                "mt-12 p-6 rounded-xl border-2 border-dashed text-center max-w-2xl mx-auto transition-all duration-300 hover:shadow-md",
                themeClasses.bgSecondary,
                themeClasses.borderSecondary,
              )}
            >
              <div className="flex justify-center mb-4">
                <div
                  className={cx(
                    "flex items-center justify-center w-16 h-16 rounded-full animate-pulse",
                    themeClasses.bgInfo,
                  )}
                >
                  <span className="text-white text-2xl">💡</span>
                </div>
              </div>
              <h3
                className={cx(
                  "text-lg font-semibold mb-2",
                  themeClasses.textPrimary,
                )}
              >
                Need additional assistance?
              </h3>
              <p
                className={cx(
                  "text-sm leading-relaxed",
                  themeClasses.textSecondary,
                )}
              >
                Our technical support team is ready to help you with any
                questions about the Performance Cockpit platform.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={cx(styles.help.layout.container, themeClasses.bgSecondary)}>
      {/* Sidebar */}
      <div
        className={cx(
          styles.help.layout.sidebar,
          themeClasses.bgSurface,
          themeClasses.borderPrimary,
        )}
      >
        <div className={styles.help.sidebar.container}>
          <h2
            className={cx(styles.help.sidebar.title, themeClasses.textPrimary)}
          >
            Help & Contact
          </h2>
          <nav className={styles.help.sidebar.nav}>
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={cx(
                  styles.help.sidebar.navItem.base,
                  activeSection === section.id
                    ? cx(
                        styles.help.sidebar.navItem.active,
                        themeClasses.bgInfoSubtle,
                        themeClasses.textInfo,
                      )
                    : cx(
                        styles.help.sidebar.navItem.inactive,
                        themeClasses.textSecondary,
                        themeClasses.hover,
                      ),
                )}
              >
                <span className={styles.help.sidebar.navItem.icon}>
                  {section.icon}
                </span>
                {section.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.help.layout.content}>
        <div
          className={cx(
            styles.help.layout.contentInner,
            themeClasses.bgPrimary,
          )}
        >
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default HelpView;
